import { useLocation } from "@reach/router"
import classNames from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"

import { TabSelectorItem } from "./TabSelectorItem"
import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import LayoutSection from "../../LayoutComponents/LayoutSection"
import LayoutItem from "../../Layouts/LayoutItem"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import {
  getBackgroundColorClass,
  getAccentColor,
  getTransparentBorderColorClass,
} from "@utils/V2/color"

type TabProps = {
  id: string
  title: string
  backgroundColor?: Color
  content: React.ReactNode
}

type AnchorProps = {
  id: string
  text: string
  tabIndex: number
}

type Props = {
  id?: string
  backgroundColor: Color
  heading?: string
  variant: "pills" | "sticky"
  tabsContent: TabProps[]
  rounded?: boolean
  onTabSelected?: (tabIndex: number, tabText?: string) => void
  anchor?: AnchorProps
  mobileGridOrder?: number
  ctaText?: string
  ctaLink?: string
  isAtTop?: boolean
  marginMobileFooter?: "default" | "80vh" | "40vh"
}

const TabSelector = ({
  id,
  backgroundColor,
  heading,
  variant = "pills",
  tabsContent,
  rounded,
  onTabSelected,
  mobileGridOrder,
  ctaText,
  ctaLink,
  isAtTop,
  marginMobileFooter,
  ...props
}: Props) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [previousSelectedTab, setPreviousSelectedTab] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const handleTabSelected = function (tabIndex: number) {
    setPreviousSelectedTab(selectedTab)
    setSelectedTab(tabIndex)

    if (variant === "sticky") {
      params.set("tab", tabsContent[tabIndex].title)
      history.replaceState(null, "", "?" + params.toString())
      if (containerRef.current) {
        const y =
          containerRef.current.getBoundingClientRect().top + window.scrollY + 30
        window.scrollTo({ top: y, behavior: "instant" })
      }
    }

    if (onTabSelected) {
      onTabSelected(tabIndex, tabsContent[tabIndex].title)
    }
  }

  useEffect(() => {
    const tab = params.get("tab")
    const tabIndex = tabsContent.findIndex(
      (tabContent) => tabContent.title === decodeURIComponent(tab ?? "")
    )

    setTimeout(() => {
      if (tabIndex !== -1 && variant === "sticky") {
        handleTabSelected(tabIndex)
      }
    }, 100)
  }, [])

  const isSticky = variant === "sticky"
  const activeBackgroundColor = tabsContent[selectedTab].backgroundColor
    ? tabsContent[selectedTab].backgroundColor
    : backgroundColor
  const activeTextColor = getAccentColor(activeBackgroundColor)

  return (
    <LayoutItem
      as={LayoutSection}
      id={id || `tab-selector-${tabsContent[selectedTab].title}`}
      primaryBackgroundColor={activeBackgroundColor}
      className={classNames({
        "!rounded-t-none": isAtTop,
        "!z-40 !rounded-t-[18px] !py-32 md:!py-[60px]": variant === "sticky",
      })}
      ref={containerRef}
      mobileGridOrder={mobileGridOrder}
      {...props}
    >
      <div
        ref={containerRef}
        className={classNames(
          activeTextColor,
          getBackgroundColorClass(activeBackgroundColor),
          getTransparentBorderColorClass(activeTextColor),
          "flex flex-col gap-16 lg:flex-row lg:gap-32",
          {
            "rounded-t-xl -mt-xl": rounded && !isSticky,
          },
          {
            [`-mt-xl sticky top-0 z-10 -ml-gutter-xs w-screen border-b px-20 sm:-ml-gutter-sm sm:px-0 md:-ml-gutter-md lg:-ml-gutter-lg xl:-ml-gutter-xl`]:
              isSticky,
          }
        )}
      >
        {/* Mobile sticky tabs - swiper menu */}
        {isSticky && (
          <div className="relative -mx-gutter-xs flex sm:-mx-gutter-sm sm:hidden md:-mx-gutter-md md:py-0 md:pb-10 lg:-mx-gutter-lg xl:-mx-gutter-xl">
            <Swiper
              className="px-gutter-xs sm:px-gutter-sm md:px-gutter-md lg:px-gutter-lg xl:px-gutter-xl"
              slidesPerView="auto"
              spaceBetween={30}
              slidesOffsetBefore={20}
            >
              {tabsContent &&
                tabsContent.map((tab, index) => (
                  <SwiperSlide
                    key={`tab-${index}`}
                    className={classNames("!w-auto")}
                  >
                    <TabSelectorItem
                      title={tab.title}
                      isSelected={selectedTab === index}
                      textColor={activeTextColor}
                      onClick={() => handleTabSelected(index)}
                      variant="underline"
                    />
                  </SwiperSlide>
                ))}

              {ctaText && ctaLink && (
                <SwiperSlide className="hidden !w-auto items-center sm:flex">
                  <TextButton
                    text={ctaText}
                    color={Color.Blue}
                    className="my-auto"
                    linkUrl={ctaLink}
                    style="icon"
                    icon={<ArrowIcon />}
                    textSize="body-md"
                  />
                </SwiperSlide>
              )}
            </Swiper>
          </div>
        )}

        {/* 
        - 'sticky' tabs for desktop
        - 'pills' tabs for both desktop and mobile 
        */}
        {!isSticky && heading && (
          <Typography
            font="grotesk"
            weight="medium"
            color={activeTextColor}
            size="lead-lg"
            text={heading}
            className="z-10"
          />
        )}

        <div
          className={classNames(
            "z-10 flex overflow-hidden",
            isSticky
              ? "ml-auto hidden gap-[30px] sm:mr-gutter-sm sm:flex md:mr-gutter-md lg:mr-gutter-lg xl:mr-gutter-xl"
              : "items-center"
          )}
        >
          {tabsContent &&
            tabsContent.map((tab, index) => (
              <TabSelectorItem
                id={tab.id}
                key={`tab-${index}`}
                title={tab.title}
                isSelected={selectedTab === index}
                textColor={activeTextColor}
                onClick={() => handleTabSelected(index)}
                variant={isSticky ? "underline" : "pill"}
              />
            ))}
          {ctaText && ctaLink && isSticky && (
            <PillButton
              style="solid"
              size="small"
              text={ctaText}
              color={Color.Blue}
              className="my-auto"
              linkUrl={ctaLink}
            />
          )}
        </div>
      </div>
      {tabsContent &&
        tabsContent.map((tab, index) => (
          <div
            key={index}
            className={classNames(
              "-mx-gutter-xs grid grid-cols-1 grid-rows-1 opacity-0 transition-all duration-200 sm:-mx-gutter-sm md:-mx-gutter-md lg:-mx-gutter-lg xl:-mx-gutter-xl", //TODO make the inverse gutter a class as we use in a bunch of places
              "-mb-[28px] md:-mb-40 lg:-mb-56", // removes white space at the bottom. White space is noticeable when tabs have opposite background colors.
              index === selectedTab
                ? "translate-y-0 opacity-100 delay-200"
                : "absolute h-0 translate-y-24 overflow-hidden opacity-0"
            )}
            onTransitionEnd={() => {
              if (index === previousSelectedTab) {
                setPreviousSelectedTab(-1)
              }
            }}
          >
            {tab.content}
          </div>
        ))}
      {ctaText && ctaLink && isSticky && (
        <div
          data-testid="sticky-container"
          className={classNames(
            marginMobileFooter !== "default"
              ? {
                  "mt-[80vh]": marginMobileFooter === "80vh",
                  "mt-[40vh]": marginMobileFooter === "40vh",
                }
              : "-mt-xl",
            "sticky bottom-0 z-10 -mx-gutter-xs flex h-72 w-screen items-center bg-white px-20 sm:-ml-gutter-sm sm:hidden md:-ml-gutter-md lg:-ml-gutter-lg xl:-ml-gutter-xl"
          )}
        >
          <PillButton
            text={ctaText}
            color={Color.Blue}
            className="my-auto"
            linkUrl={ctaLink}
            style="solid"
            size={"large"}
          />
        </div>
      )}
    </LayoutItem>
  )
}

export default TabSelector
